import React from 'react';
import { Link } from 'react-router-dom';
import { DataTitle, DataValue, NoData, Poppins } from 'src/common';
import ExpandSection from 'src/components/ExpandSection';
import { ControlQuery } from 'src/graphql/_generated';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import styled from 'styled-components';
import { SuggestedFrameworks } from './comps';
import { useAuth } from 'src/state/auth';

const Div = styled.div`
  .grid {
    max-width: 1100px;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    grid-gap: 70px;

    &--divider {
      width: 1px;
      background: ${({ theme }) => theme.colors.stroke};
    }
    &--item {
      display: flex;
      flex-direction: column;
      gap: 8px;

      &--row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 20px;

        &--item {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .grid {
      grid-gap: 20px;
      grid-template-columns: 1fr;
      &--divider {
        display: none;
      }
    }
  }
`;

interface FrameworksSectionProps {
  control: NonNullable<ControlQuery['control']>;
}

export const FrameworksSection: React.FC<FrameworksSectionProps> = ({ control }) => {
  const { features } = useAuth();

  const sourceFramework = control.frameworkControls[0];

  type Grop = {
    framework: NonNullable<(typeof sourceFramework.compatibleControls)[number]['controlFramework']>;
    controls: typeof sourceFramework.compatibleControls;
  };

  const groups: Grop[] = sourceFramework?.compatibleControls.reduce((acc, control) => {
    if (!control.controlFramework) return acc;

    const frameworkId = control.controlFramework.id;
    const existingGroup = acc.find((g) => g.framework.id === frameworkId);

    if (existingGroup) {
      existingGroup.controls.push(control);
    } else {
      acc.push({
        framework: control.controlFramework,
        controls: [control],
      });
    }

    return acc;
  }, [] as Grop[]);
  return (
    <>
      <ExpandSection title="FRAMEWORKS" sectionId="control_frameworks">
        <Div>
          {!!sourceFramework ? (
            <>
              <div className="grid">
                <div className="grid--item">
                  <Poppins color="prussianBlue" px={14} weight={500}>
                    SOURCE
                  </Poppins>
                  <div className="grid--item--row">
                    <div className="grid--item--row--item">
                      <DataTitle $noMargin>CONTROL ID</DataTitle>
                      <DataValue>
                        <Link to={`/control-frameworks/all-controls/${sourceFramework.id}`}>
                          {sourceFramework.control_identifier}
                        </Link>
                      </DataValue>
                    </div>
                    <div className="grid--item--row--item" style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <div>
                        <DataTitle>FRAMEWORK</DataTitle>
                        <div>
                          <DataValue>
                            <Link
                              to={`/control-frameworks/${sourceFramework.controlFramework?.id}`}
                              data-cy="framework-link"
                              onClick={() =>
                                mpEvent(MPEvents.Link, {
                                  source: {
                                    value: ['Control'],
                                  },
                                  destination: {
                                    value: ['Framework'],
                                    params: { id: sourceFramework.controlFramework?.id },
                                  },
                                  tags: ['RMP'],
                                })
                              }
                            >
                              {sourceFramework.controlFramework?.shortname}
                            </Link>
                          </DataValue>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {features['app.control.suggested-frameworks'] && groups && groups.length > 0 && (
                  <>
                    <div className="grid--divider" />
                    <div className="grid--item">
                      <Poppins color="prussianBlue" px={14} weight={500}>
                        RELATED
                      </Poppins>
                      {groups.map((group) => (
                        <div key={group.framework.id} className="grid--item--row">
                          <div className="grid--item--row--item">
                            <DataTitle $noMargin>CONTROL ID(s)</DataTitle>
                            <DataValue>
                              {group.controls.map((control, controlIdx) => (
                                <React.Fragment key={control.id}>
                                  <Link to={`/control-frameworks/all-controls/${control.id}`}>
                                    {control.control_identifier}
                                  </Link>
                                  {controlIdx < group.controls.length - 1 && ', '}
                                </React.Fragment>
                              ))}
                            </DataValue>
                          </div>
                          <div className="grid--item--row--item" style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <div>
                              <DataTitle>FRAMEWORK</DataTitle>
                              <DataValue>
                                <Link to={`/control-frameworks/${group.framework.id}`}>
                                  {group.framework.shortname}
                                </Link>
                              </DataValue>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              {features['app.control.suggested-frameworks'] ? (
                <SuggestedFrameworks control={control} />
              ) : (
                <NoData title="No frameworks" />
              )}
            </>
          )}
        </Div>
      </ExpandSection>
    </>
  );
};
